<script lang="ts" setup>
import type { PropType } from 'vue'
import type { TNumber } from './UiNumbers.types'
defineProps({
  numbers: {
    type: Array as PropType<TNumber[]>,
    default: [] as TNumber[],
  },
  type: {
    type: String,
    default: 'list',
  },
  layout: {
    type: String,
    default: 'default',
  },
  color: {
    type: String,
    default: 'blue',
  },
})
</script>

<template>
  <div
    :class="[
      'numbers',
      `numbers--${type}`,
      `numbers--${layout}`
    ]"
  >
    <div
      v-for="(item, index) in numbers"
      :key="index"
      class="numbers__item"
    >
      <h3
        :class="[
          'numbers__number',
          `numbers__number--${color}`
        ]"
        v-html="item.number"
      />
      
      <span
        class="numbers__text"
        v-html="item.text"
      />
      <span
        v-if="item.subtext"
        class="numbers__subtext"
      >
        {{ item.subtext }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .numbers {
    $root: &;

    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    width: 100%;
    margin-top: 3.2rem;

    @include atXS {
      flex-direction: row;
      justify-content: space-between;
      align-items: stretch;
    }

    @include atM {
      margin-top: 7.2rem;
    }

    &--default {
      @include atXS {
        gap: 4rem;
      }
    }

    &--box {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1.6rem;

      @include atM {
        margin-top: 0;
      }

      #{$root}__item {
        flex-direction: column;
        justify-content: flex-start;
        width: calc(50% - 0.8rem);
        padding: 1.6rem 1.6rem 2.4rem 1.6rem;
        text-align: left;
        border: 1px solid $gray-300;
        border-radius: 1.6rem;

        @include atXS {
          align-items: flex-start;
        }

        @include atM {
          padding: 3.2rem 2.4rem;
        }

        @include atXL {
          padding: 3.2rem 5rem 4rem 4rem;
        }
      }

      #{$root}__text {
        font-size: 1.4rem;

        @include atM {
          font-size: 2rem;
          line-height: 2.8rem;
        }
      }
    }

    &--layout-1 {
      gap: 0.8rem;
      margin-top: 2.4rem;

      @include atM {
        flex-direction: column;
      }

      #{$root}__item {
        justify-content: flex-start;
        align-items: center;
        padding: 1.6rem;
        background-color: $white;
        border: 1px solid $gray-300;
        border-radius: 1.6rem;

        @include atM {
          flex-direction: row;
          width: 100%;
          text-align: left;
        }
      }

      #{$root}__number {
        @include atM {
          font-size: 2.4rem;
          line-height: 3.2rem;
        }
      }
    }

    &--vertical {
      gap: 3.2rem;

      #{$root}__item {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      #{$root}__number {
        text-align: center;
      }
    }

    &__item {
      display: flex;
      align-items: baseline;
      gap: 1.6rem;

      @include atXS {
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        gap: 2.4rem;
        width: calc(25% - 2rem);
        text-align: center;
      }
    }

    &__number {
      color: var(--brand-color);
      font-weight: 500;
      font-size: 2.8rem;
      line-height: 3.6rem;
      white-space: nowrap;

      &--blue {
        color: $blue-400;
      }

      &--lightblue {
        color: $lightblue-400;
      }

      &--purple {
        color: $purple-400;
      }

      @include atM {
        font-size: 4rem;
        line-height: 4.8rem;
      }
    }

    &__text {
      font-weight: 500;
      font-size: 1.6rem;
      line-height: 2.4rem;

      @include atM {
        font-size: 1.8rem;
        line-height: 2.4rem;
      }
    }

    &__subtext {
      @include body-2-medium;
      color: $text-200;
    }
  }
</style>
